import { getContrastRatio, getLuminance, lighten, darken } from '@mui/material/styles';
import type { Palette } from '@mui/material/styles';
import { useL2DTheme } from './useL2DTheme';

export type ThemeContrastPriority = 'primary' | 'secondary' | 'text' | 'background';

export type ThemeColor = 'primary' | 'secondary' | 'warning' | 'success' | 'error' | 'info';

export type ComponentColorResult = {
    /** Solid background tint — use for fallback or box-shadow color values. */
    backgroundColor: string;
    /** Solid tint for the hover state. */
    backgroundColorOnHover: string;
    /** CSS gradient (use as `background`, not `backgroundColor`). */
    backgroundGradient: string;
    /** CSS gradient for the hover state. */
    backgroundGradientOnHover: string;
    /** Glassmorphism style object — spread directly into an `sx` prop. */
    glassyStyle: GlassyStyle;
    /** Glassmorphism style for the hover state. */
    glassyStyleOnHover: GlassyStyle;
    /** Text color — the semantic color itself, contrast-adjusted if needed. */
    textColor: string;
    /** Text color for the hover state. */
    textColorOnHover: string;
};

/**
 * Style object produced by `computeStyledBackground`. Spread directly into an
 * `sx` prop — compatible with any MUI component.
 */
export type GlassyStyle = {
    background: string;
};

/**
 * Produces a simple horizontal gradient tint for colored UI components.
 *
 * The gradient goes left-to-right (slightly lighter → slightly more saturated),
 * using the same coefficient range as `computeGradientBackground` but with a
 * horizontal direction.
 *
 * @param colorMain - Resolved hex color
 * @param mode      - Current palette mode
 * @param hover     - Whether to produce the hover-state variant
 */
export const computeGlassyStyle = (
    colorMain: string,
    mode: 'light' | 'dark',
    hover = false,
): GlassyStyle => {
    const spread = 0.06;
    if (mode === 'dark') {
        const center = hover ? 0.6 : 0.5;
        return {
            background: `linear-gradient(135deg, ${darken(colorMain, Math.max(0, center - spread))}, ${darken(colorMain, Math.min(1, center + spread))})`,
        };
    }
    const center = hover ? 0.78 : 0.88;
    return {
        background: `linear-gradient(135deg, ${lighten(colorMain, Math.min(1, center + spread))}, ${lighten(colorMain, Math.max(0, center - spread))})`,
    };
};

/**
 * Resolves a raw color string to its `main` hex value using the MUI palette.
 *
 * Resolution order:
 * 1. Known MUI semantic keys (`primary`, `warning`, …)
 * 2. Any palette extension that has a `.main` property (custom colors like
 *    `purple`, `amber`, `deepOrange`, `blueGrey`, …)
 * 3. Raw CSS color string (hex, rgb, hsl…) — passed through as-is
 */
export const resolveColorMain = (color: string, palette: Palette): string => {
    const paletteEntry = palette[color as keyof Palette];
    if (paletteEntry && typeof paletteEntry === 'object' && 'main' in paletteEntry) {
        return (paletteEntry as { main: string }).main;
    }
    return color;
};

/**
 * Produces a subtle CSS linear-gradient from a resolved color.
 *
 * The gradient spans a ±`spread` range around `bgCoeff` (lighten in light
 * mode, darken in dark mode), giving a gentle depth effect without altering
 * the perceived average lightness.
 *
 * @param colorMain - Resolved hex color
 * @param mode      - Current palette mode
 * @param bgCoeff   - Center coefficient (default 0.88 for light, 0.5 for dark)
 * @param spread    - Half-width of the gradient range (default 0.06)
 */
export const computeGradientBackground = (
    colorMain: string,
    mode: 'light' | 'dark',
    bgCoeff?: number,
    spread = 0.06,
): string => {
    if (mode === 'dark') {
        const center = bgCoeff ?? 0.5;
        const from = darken(colorMain, Math.max(0, center - spread));
        const to = darken(colorMain, Math.min(1, center + spread));
        return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
    }
    const center = bgCoeff ?? 0.88;
    const from = lighten(colorMain, Math.min(1, center + spread));
    const to = lighten(colorMain, Math.max(0, center - spread));
    return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
};

// Luminance max en dark mode (évite d'aller vers le blanc),
// Luminance min en light mode (évite d'aller vers le noir)
const LUMINANCE_CAP = {
    dark: 0.65,
    light: 0.12,
};

/**
 * Retourne une variante de `color` ajustée (lightened/darkened) pour qu'elle
 * contraste suffisamment avec `backgroundColor`.
 *
 * Utile pour les composants dont la couleur est sémantiquement fixée (badge warning,
 * chip error…) mais qui n'ont pas assez de contraste avec le fond de l'appli.
 *
 * Le `mode` du thème est pris en compte pour éviter de trop éclaircir en dark mode
 * (pas de couleurs trop proches du blanc) et de trop assombrir en light mode.
 *
 * @param color           - Couleur du composant (ex: palette.warning.main)
 * @param backgroundColor - Couleur de fond sur lequel il est posé
 * @param threshold       - Ratio de contraste minimum (défaut : 3 — WCAG AA large)
 * @param mode            - 'light' | 'dark' (défaut : 'light')
 */
export const getContrastingVariant = (
    color: string,
    backgroundColor: string,
    threshold = 3,
    mode: 'light' | 'dark' = 'light',
): string => {
    if (getContrastRatio(backgroundColor, color) >= threshold) return color;

    const bgIsDark = getLuminance(backgroundColor) < 0.5;
    const adjust = bgIsDark ? lighten : darken;
    const luminanceCap = mode === 'dark' ? LUMINANCE_CAP.dark : LUMINANCE_CAP.light;

    const step = 0.05;
    let best = color;
    let bestRatio = getContrastRatio(backgroundColor, color);

    for (let i = step; i <= 1; i += step) {
        const adjusted = adjust(color, i);
        const luminance = getLuminance(adjusted);
        const ratio = getContrastRatio(backgroundColor, adjusted);

        if (ratio > bestRatio) {
            best = adjusted;
            bestRatio = ratio;
        }

        if (ratio >= threshold) return adjusted;

        // Arrêt si on dépasse la luminance acceptable pour ce mode
        const exceedsCap = bgIsDark ? luminance > luminanceCap : luminance < luminanceCap;
        if (exceedsCap) break;
    }

    return best;
};

/**
 * Pure function that computes a background + text color pair for a UI component
 * (chip, button badge, icon container…) given a semantic or arbitrary color.
 *
 * Strategy:
 * - Background is a pale tint of the color (light mode) or a darkened variant
 *   (dark mode), so the component reads as "this color" without overwhelming.
 * - Text color starts from the semantic color itself and is contrast-adjusted
 *   to reach WCAG AA Large (ratio ≥ 3) against the computed background,
 *   preserving the brand hue as much as possible.
 *
 * @param colorMain  - Resolved hex color (use `resolveColorMain` first)
 * @param mode       - Current palette mode
 * @param bgCoeff    - Lighten coefficient for the normal background (default 0.88)
 * @param hoverCoeff - Lighten coefficient for the hover background (default 0.78)
 */
export const computeComponentColors = (
    colorMain: string,
    mode: 'light' | 'dark',
    bgCoeff = 0.88,
    hoverCoeff = 0.78,
): ComponentColorResult => {
    const backgroundColor = mode === 'light' ? lighten(colorMain, bgCoeff) : darken(colorMain, 0.5);
    const backgroundColorOnHover =
        mode === 'light' ? lighten(colorMain, hoverCoeff) : darken(colorMain, 0.6);

    const backgroundGradient = computeGradientBackground(colorMain, mode, bgCoeff);
    const backgroundGradientOnHover = computeGradientBackground(colorMain, mode, hoverCoeff);

    const glassyStyle = computeGlassyStyle(colorMain, mode);
    const glassyStyleOnHover = computeGlassyStyle(colorMain, mode, true);

    const textColor = getContrastingVariant(colorMain, backgroundColor, 3, mode);
    const textColorOnHover = getContrastingVariant(colorMain, backgroundColorOnHover, 3, mode);

    return {
        backgroundColor,
        backgroundColorOnHover,
        backgroundGradient,
        backgroundGradientOnHover,
        glassyStyle,
        glassyStyleOnHover,
        textColor,
        textColorOnHover,
    };
};

/**
 * Hook that computes background + text colors for a colored UI component.
 *
 * Accepts either a ThemeColor key (`'primary'`, `'warning'`, …) or any valid
 * CSS color string (hex, rgb…).
 *
 * @example
 * const { backgroundColor, textColor } = useComponentColor('warning');
 * const { backgroundColor, textColor } = useComponentColor('#e67e22');
 */
export const useComponentColor = (color: string): ComponentColorResult => {
    const { palette } = useL2DTheme();
    const colorMain = resolveColorMain(color, palette);
    return computeComponentColors(colorMain, palette.mode);
};

/**
 * Retourne la couleur du thème qui contraste le mieux avec `backgroundColor`,
 * en respectant un seuil de contraste minimum (WCAG AA).
 *
 * Contrairement à `palette.getContrastText()` qui choisit uniquement entre
 * noir et blanc, cette fonction parcourt les couleurs sémantiques du thème
 * (texte, fonds, primary, secondary) et retourne la première qui atteint
 * le seuil requis.
 *
 * Seuils recommandés :
 * - 4.5 : texte courant (WCAG AA)
 * - 3.0 : grands titres ou composants UI (WCAG AA large)
 *
 * @param backgroundColor - Couleur de fond (hex, rgb…)
 * @param palette         - Palette MUI courante
 * @param threshold       - Ratio de contraste minimum (défaut : 4.5)
 * @param priority        - Groupe de couleurs à tester en priorité ('primary' | 'secondary' | 'text' | 'background')
 */
export const getThemeContrastColor = (
    backgroundColor: string,
    palette: Palette,
    threshold = 4.5,
    priority?: ThemeContrastPriority,
): string => {
    const priorityGroups: Record<ThemeContrastPriority, string[]> = {
        primary: [palette.primary.main, palette.primary.light, palette.primary.dark],
        secondary: [palette.secondary.main, palette.secondary.light, palette.secondary.dark],
        text: [palette.text.primary, palette.text.secondary],
        background: [palette.background.paper, palette.background.default],
    };

    // Candidats ordonnés par priorité sémantique (sans le groupe prioritaire)
    const defaultCandidates: string[] = [
        palette.text.primary,
        palette.text.secondary,
        palette.background.paper,
        palette.background.default,
        palette.primary.main,
        palette.primary.light,
        palette.primary.dark,
        palette.secondary.main,
        palette.secondary.light,
        palette.secondary.dark,
    ];

    // Si une priorité est spécifiée, tester ce groupe d'abord
    if (priority) {
        const priorityCandidates = priorityGroups[priority];
        const priorityMatch = priorityCandidates.find(
            (color) => getContrastRatio(backgroundColor, color) >= threshold,
        );
        if (priorityMatch) return priorityMatch;
    }

    const accessible = defaultCandidates.filter(
        (color) => getContrastRatio(backgroundColor, color) >= threshold,
    );

    if (accessible.length > 0) {
        return accessible[0];
    }

    // Fallback : meilleur contraste disponible parmi les candidats
    const best = defaultCandidates.reduce((current, color) =>
        getContrastRatio(backgroundColor, color) > getContrastRatio(backgroundColor, current)
            ? color
            : current,
    );

    // Si même le meilleur candidat ne passe pas le seuil (ex: warning yellow),
    // on ajuste la couleur d'entrée elle-même (lighten/darken) jusqu'à obtenir
    // un bon contraste avec elle-même
    if (getContrastRatio(backgroundColor, best) < threshold) {
        const bgIsDark = getLuminance(backgroundColor) < 0.5;
        const adjust = bgIsDark ? lighten : darken;
        const step = 0.05;

        for (let i = step; i <= 1; i += step) {
            const adjusted = adjust(backgroundColor, i);
            if (getContrastRatio(backgroundColor, adjusted) >= threshold) {
                return adjusted;
            }
        }

        // Dernier recours : blanc ou noir selon la luminosité du fond
        return bgIsDark ? '#ffffff' : '#000000';
    }

    return best;
};

/**
 * Hook React exposant `getContrastingVariant` avec le mode du thème courant
 * injecté automatiquement.
 *
 * @example
 * const contrastingVariant = useContrastingVariant();
 * // badge warning visible en dark mode sans être trop blanc
 * <Chip sx={{ backgroundColor: contrastingVariant(palette.warning.main, appBackground) }} />
 */
export const useContrastingVariant = (): ((
    color: string,
    backgroundColor: string,
    threshold?: number,
) => string) => {
    const { palette } = useL2DTheme();
    return (color: string, backgroundColor: string, threshold?: number) =>
        getContrastingVariant(color, backgroundColor, threshold, palette.mode);
};

/**
 * Retourne une fonction qui applique un tint adapté au mode courant.
 *
 * - Light mode : `lighten(color, 0.7)` — teinte très pâle sur fond clair
 * - Dark mode  : `darken(color, 0.4)`  — teinte plus sombre sur fond sombre
 *
 * Utile pour les backgrounds d'items colorés (badges, cards, list items…)
 * où on veut une teinte de la couleur sémantique sans transparence.
 *
 * @example
 * const tint = usePaletteTint();
 * <Box sx={{ backgroundColor: tint(palette.warning.main) }} />
 */
export const usePaletteTint = (): ((color: string) => string) => {
    const { palette } = useL2DTheme();
    return palette.mode === 'dark'
        ? (color: string) => darken(color, 0.4)
        : (color: string) => lighten(color, 0.7);
};

/**
 * Hook React exposant `getThemeContrastColor` avec la palette du thème courant
 * injectée automatiquement.
 *
 * @example
 * const contrastColor = useThemeContrastColor();
 * <Box color={contrastColor(palette.primary.main)}>Texte lisible</Box>
 *
 * // Avec seuil personnalisé (3.0 pour composants UI)
 * <Box color={contrastColor(someColor, 3)}>Label bouton</Box>
 *
 * // Avec priorité (ex: CTA → couleurs primary en premier)
 * <Box color={contrastColor(someColor, 4.5, 'primary')}>Label CTA</Box>
 */
export const useThemeContrastColor = (): ((
    backgroundColor: string,
    threshold?: number,
    priority?: ThemeContrastPriority,
) => string) => {
    const { palette } = useL2DTheme();
    return (backgroundColor: string, threshold?: number, priority?: ThemeContrastPriority) =>
        getThemeContrastColor(backgroundColor, palette, threshold, priority);
};
